import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { SUPPORT_EMAIL } from '../config';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  Box,
  Text,
  Button,
  InlineStack,
  Banner,
  Spinner,
  Link,
} from '@shopify/polaris';
import { ScanProgressBar } from '../components/ScanProgressBar';
import { ScanMessages } from '../components/ScanMessages';
import { useScanStatus } from '../hooks/useScanStatus';
import { useScanMessages } from '../hooks/useScanMessages';
import { cancelScan } from '../services/api';
import { trackScanProgressSeen, trackScanFailed } from '../services/analytics';
import { PageEntrance, fireConfetti } from '../components/ui';
import './ScanProgressPage.css';

/** T053: How many consecutive fetch errors before showing connectivity warning */
const CONNECTIVITY_ERROR_THRESHOLD = 3;

/** Completion animation phases */
type CompletionPhase = 'filling' | 'celebrating' | null;

/**
 * T029 + T059: ScanProgressPage.
 * Progress bar, ETA, rotating messages, "Go to dashboard" link,
 * cancel button, and auto-transition to scan-complete on COMPLETED status.
 * T052: Enhanced scan failed screen with support contact.
 * T053: Connectivity error detection and warning.
 */
export function ScanProgressPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isOnboarding = (location.state as { fromOnboarding?: boolean } | null)?.fromOnboarding === true;
  const { data: scanProgress, isLoading, isError } = useScanStatus(jobId ?? null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // T053: Track consecutive connectivity errors
  const consecutiveErrors = useRef(0);
  const [showConnectivityWarning, setShowConnectivityWarning] = useState(false);

  useEffect(() => {
    if (isError) {
      consecutiveErrors.current += 1;
      if (consecutiveErrors.current >= CONNECTIVITY_ERROR_THRESHOLD) {
        setShowConnectivityWarning(true);
      }
    } else if (scanProgress) {
      consecutiveErrors.current = 0;
      setShowConnectivityWarning(false);
    }
  }, [isError, scanProgress]);

  // T055: Track progress page view once
  useEffect(() => {
    trackScanProgressSeen();
  }, []);

  // T055: Track scan failure
  useEffect(() => {
    if (scanProgress?.status === 'FAILED') {
      trackScanFailed(scanProgress.errorMessage ?? 'UNKNOWN');
    }
  }, [scanProgress?.status, scanProgress?.errorMessage]);

  const isActive =
    scanProgress?.status === 'QUEUED' || scanProgress?.status === 'RUNNING';
  const { message, messageIndex, totalMessages } = useScanMessages(isActive);

  const handleCancel = useCallback(async () => {
    if (!jobId) return;
    setIsCancelling(true);
    setCancelError(null);
    try {
      await cancelScan(jobId);
    } catch (err: unknown) {
      setCancelError(err instanceof Error ? err.message : 'Failed to cancel scan');
    } finally {
      setIsCancelling(false);
    }
  }, [jobId]);

  // Graceful completion phases: null → 'filling' (bar → 100%) → 'celebrating' (confetti) → redirect
  const [completionPhase, setCompletionPhase] = useState<CompletionPhase>(null);
  const completionStarted = useRef(false);

  useEffect(() => {
    if (scanProgress?.status === 'COMPLETED' && jobId && !completionStarted.current) {
      completionStarted.current = true;

      // Phase 1: Let the progress bar animate to 100%
      setCompletionPhase('filling');

      setTimeout(() => {
        // Phase 2: Fire confetti and show success message
        setCompletionPhase('celebrating');
        void fireConfetti('medium');
      }, 1000);

      setTimeout(() => {
        globalThis.location.href = '/scan-complete/' + jobId;
      }, 3500);
    }
  }, [scanProgress?.status, jobId]);

  const isCompleting = completionPhase === 'filling' || completionPhase === 'celebrating';

  // Redirect on cancel
  if (scanProgress?.status === 'CANCELLED') {
    // Show briefly, then allow user to navigate
  }

  if (isLoading || !scanProgress) {
    return (
      <Page title="Scan in Progress">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center" gap="200">
                <Spinner size="small" />
                <Text as="p" variant="bodyMd">
                  Loading scan progress…
                </Text>
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (scanProgress.status === 'FAILED') {
    return (
      <Page title="Scan Failed">
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Banner tone="critical">
                  <p>
                    {scanProgress.errorMessage ?? 'The scan encountered an unexpected error.'}
                  </p>
                </Banner>
                <Text as="p" variant="bodySm" tone="subdued">
                  Scan ID: {jobId}
                </Text>
                <InlineStack gap="200">
                  <Button variant="primary" onClick={() => navigate('/')}>
                    Try again
                  </Button>
                  <Button onClick={() => navigate('/dashboard')}>
                    Go to dashboard
                  </Button>
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">
                  If this problem persists,{' '}
                  <Link
                    url={`mailto:${SUPPORT_EMAIL}?subject=Scan%20failure%20${jobId}`}
                    target="_blank"
                  >
                    contact support
                  </Link>{' '}
                  and include your scan ID.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (scanProgress.status === 'CANCELLED') {
    return (
      <Page title="Scan Cancelled">
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Scan cancelled
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {scanProgress.scannedProducts} of {scanProgress.totalProducts} products
                  were scanned. Partial results are available on the dashboard.
                </Text>
                <InlineStack gap="200">
                  <Button variant="primary" onClick={() => navigate('/dashboard')}>
                    View partial results
                  </Button>
                  <Button onClick={() => navigate('/')}>
                    Start new scan
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <PageEntrance>
    <Page title={isCompleting ? 'Scan complete!' : 'Scanning your products'}>
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <ScanProgressBar scanProgress={scanProgress} />
              {completionPhase === 'celebrating' || completionPhase === 'filling' ? (
                <Box paddingBlockStart="400" paddingBlockEnd="400">
                  <BlockStack gap="200" align="center">
                    <Text as="p" variant="headingMd" tone="success" alignment="center">
                      Scan complete!
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                      Preparing your results…
                    </Text>
                  </BlockStack>
                </Box>
              ) : (
                <ScanMessages
                  message={message}
                  messageIndex={messageIndex}
                  totalMessages={totalMessages}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {cancelError && (
          <Layout.Section>
            <Banner tone="warning" onDismiss={() => setCancelError(null)}>
              <p>{cancelError}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* T053: Connectivity warning after repeated fetch failures */}
        {showConnectivityWarning && (
          <Layout.Section>
            <Banner tone="warning">
              <p>
                Having trouble connecting to the server. Your scan is still running
                in the background — we'll update progress when the connection is
                restored.
              </p>
            </Banner>
          </Layout.Section>
        )}

        {!isCompleting && !isOnboarding && (
        <Layout.Section>
          <InlineStack align="space-between">
            <Button variant="plain" onClick={() => navigate('/dashboard')}>
              Go to dashboard
            </Button>
            <Button
              tone="critical"
              onClick={handleCancel}
              disabled={isCancelling}
              loading={isCancelling}
            >
              Cancel scan
            </Button>
          </InlineStack>
        </Layout.Section>
        )}

        {!isCompleting && !isOnboarding && (
        <Layout.Section>
          <Banner tone="info">
            <p>
              The scan continues in the background — you can close the app and come
              back later.
            </p>
          </Banner>
        </Layout.Section>
        )}
      </Layout>
    </Page>
    </PageEntrance>
  );
}
